import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button, FormGroup, Input, Label } from "reactstrap";
import {
  listCollectorPhones,
  sendMergeOtp,
  sendPhoneChangeOtp,
  verifyMerge,
  verifyPhoneChange,
} from "../../services/collectorApi";
import { clearCollectorToken, setCollectorToken } from "../../helpers/collectorSession";

type PhoneRow = { phone: string; current: boolean; activeFrom?: string; activeUntil?: string };
/** Number change only: proof that the signed-in person holds the current number. */
type CurrentProof = { currentOtp?: string; passkey?: string };
type Feedback = { type: "success" | "danger"; text: string } | null;

const ERRORS: Record<string, string> = {
  PHONE_IN_USE: "This number signs in to another HKP account. You can merge that account into this one instead.",
  SAME_NUMBER: "That is already your number on this account.",
  NO_ACCOUNT_FOR_NUMBER: "No other HKP account signs in with this number.",
  TOO_MANY_CODES: "Too many codes requested. Please try again in an hour.",
  TOO_MANY_ATTEMPTS: "Too many wrong tries. Request new codes, or try again in an hour.",
  CURRENT_NUMBER_PROOF_REQUIRED: "Enter the code sent to your current number, or your passkey if you no longer have that number.",
  WRONG_PASSKEY: "That passkey is not right.",
  SESSION_EXPIRED: "Your number changed on another device. Please sign in again.",
};

const errorText = (e: any, fallback: string) => {
  const msg = e?.response?.data?.message || "";
  return ERRORS[msg] || msg || fallback;
};

/** Shows +91 98765 43210 for Indian numbers; other numbers as stored. */
export const formatPhone = (digits: string) =>
  digits.length === 12 && digits.startsWith("91") ? `+91 ${digits.slice(2, 7)} ${digits.slice(7)}` : `+${digits}`;

const when = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : "");

/** Enter a number, get a code on it, enter the code. Shared by number change and merge. */
const OtpNumberStep: React.FC<{
  idPrefix: string;
  numberLabel: string;
  sendLabel: string;
  confirmLabel: string;
  send: (phone: string) => Promise<unknown>;
  verify: (phone: string, otp: string, proof: CurrentProof) => Promise<any>;
  /** Number change: also prove the current number (its code, or the passkey). */
  currentNumber?: string;
  hasPasskey?: boolean;
  onDone: (data: any) => void;
  onCancel: () => void;
  initialPhone?: string;
  /** Offered when the number already signs in to another account. */
  onPhoneInUse?: (phone: string) => void;
}> = ({ idPrefix, numberLabel, sendLabel, confirmLabel, send, verify, onDone, onCancel, initialPhone, onPhoneInUse,
  currentNumber, hasPasskey }) => {
  const [phone, setPhone] = useState(initialPhone || "");
  const [currentOtp, setCurrentOtp] = useState("");
  const [usePasskey, setUsePasskey] = useState(false);
  const [passkey, setPasskey] = useState("");
  const [inUse, setInUse] = useState(false);
  const [otp, setOtp] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const sendCode = async () => {
    if (phone.replace(/\D/g, "").length < 10) {
      setError("Enter a valid phone number");
      return;
    }
    setBusy(true);
    setError("");
    setInUse(false);
    try {
      await send(phone);
      setCodeSent(true);
      setOtp("");
      setCurrentOtp("");
    } catch (e: any) {
      setInUse(e?.response?.data?.message === "PHONE_IN_USE");
      setError(errorText(e, "Could not send the code"));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      const proof: CurrentProof = usePasskey ? { passkey } : { currentOtp: currentOtp.trim() };
      const res = await verify(phone, otp.trim(), currentNumber ? proof : {});
      onDone(res.data?.data || {});
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      if (msg === "TOO_MANY_ATTEMPTS") setCodeSent(false);
      setError(errorText(e, "Could not verify the code"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2">
      <FormGroup>
        <Label for={`${idPrefix}Phone`}>{numberLabel}</Label>
        <Input
          id={`${idPrefix}Phone`}
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          disabled={codeSent}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="98765 43210"
        />
      </FormGroup>
      {codeSent && (
        <FormGroup>
          <Label for={`${idPrefix}Otp`}>Code sent to {phone}</Label>
          <Input
            id={`${idPrefix}Otp`}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          />
          <Button color="link" className="p-0 small" disabled={busy} onClick={sendCode}>
            {currentNumber ? "Send new codes" : "Send a new code"}
          </Button>
        </FormGroup>
      )}
      {codeSent && currentNumber && !usePasskey && (
        <FormGroup>
          <Label for={`${idPrefix}CurrentOtp`}>Code sent to your current number {currentNumber}</Label>
          <Input
            id={`${idPrefix}CurrentOtp`}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={currentOtp}
            onChange={(e) => setCurrentOtp(e.target.value.replace(/\D/g, ""))}
          />
          {hasPasskey ? (
            <Button color="link" className="p-0 small" onClick={() => setUsePasskey(true)}>
              I no longer have this number
            </Button>
          ) : (
            <small className="text-muted d-block">
              No longer have this number? Set a passkey first, or contact HKP support.
            </small>
          )}
        </FormGroup>
      )}
      {codeSent && currentNumber && usePasskey && (
        <FormGroup>
          <Label for={`${idPrefix}Passkey`}>Your account passkey</Label>
          <Input
            id={`${idPrefix}Passkey`}
            type="password"
            autoComplete="current-password"
            value={passkey}
            onChange={(e) => setPasskey(e.target.value)}
          />
          <Button color="link" className="p-0 small" onClick={() => setUsePasskey(false)}>
            Use the code sent to my current number
          </Button>
        </FormGroup>
      )}
      {error && <p className="mc-field-error mb-2">{error}</p>}
      {inUse && onPhoneInUse && (
        <Button className="mc-btn-secondary mb-2" onClick={() => onPhoneInUse(phone)}>
          Merge that account instead
        </Button>
      )}
      <div className="mc-form-actions">
        {codeSent ? (
          <Button
            className="mc-btn-primary"
            onClick={confirm}
            disabled={busy || otp.length < 4 || (Boolean(currentNumber) && (usePasskey ? !passkey : currentOtp.length < 4))}
          >
            {busy ? "Checking…" : confirmLabel}
          </Button>
        ) : (
          <Button className="mc-btn-primary" onClick={sendCode} disabled={busy}>
            {busy ? "Sending…" : sendLabel}
          </Button>
        )}
        <Button className="mc-btn-ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
      </div>
    </div>
  );
};

/**
 * Phone number for My Collection. The account stays the same when the number changes, and
 * purchases billed to any number the account has had stay in the collection.
 */
const CollectorPhoneSettings: React.FC<{ currentPhone?: string; hasPasskey?: boolean; onAccountChanged: () => void }> = ({
  currentPhone,
  hasPasskey,
  onAccountChanged,
}) => {
  const [phones, setPhones] = useState<PhoneRow[]>([]);
  const [mode, setMode] = useState<"" | "change" | "merge">("");
  const [mergePhone, setMergePhone] = useState("");
  const [msg, setMsg] = useState<Feedback>(null);
  const history = useHistory();

  const loadPhones = () =>
    listCollectorPhones()
      .then((res) => setPhones(res.data?.data?.items || []))
      .catch(() => setPhones([]));

  useEffect(() => {
    loadPhones();
  }, []);

  const finish = (data: any, text: string, numberChanged = false) => {
    // A number change or merge ends every other session; the server hands this device a fresh pair.
    // Without one this device's session is over too, so say why and go to sign-in.
    if (!data?.token) {
      clearCollectorToken(numberChanged ? "number-changed" : "refused");
      history.push("/my-collection/login");
      return;
    }
    setCollectorToken(data.token, data.refreshToken);
    setMode("");
    setMsg({ type: "success", text });
    loadPhones();
    onAccountChanged();
  };

  const past = phones.filter((p) => !p.current);

  return (
    <section className="mc-card">
      <h2 className="h6 text-uppercase text-muted mb-1">Phone number</h2>
      <p className="small text-muted">
        You sign in with this number. If you change it, your collection stays the same, and purchases
        billed to your old numbers still show up here.
      </p>
      {msg && <Alert color={msg.type}>{msg.text}</Alert>}
      <p className="mb-2">
        <span className="fw-semibold">{currentPhone ? formatPhone(currentPhone.replace(/\D/g, "")) : "—"}</span>
      </p>
      {past.length > 0 && (
        <div className="mb-3">
          <div className="small text-muted mb-1">Earlier numbers (they no longer sign in)</div>
          <ul className="list-unstyled small mb-0">
            {past.map((p) => (
              <li key={`${p.phone}-${p.activeFrom || ""}`}>
                {formatPhone(p.phone)}
                {p.activeUntil ? <span className="text-muted"> · until {when(p.activeUntil)}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {mode === "" && (
        <div className="d-flex flex-wrap gap-2">
          <Button className="mc-btn-secondary" onClick={() => { setMsg(null); setMode("change"); }}>
            Change number
          </Button>
          <Button className="mc-btn-ghost" onClick={() => { setMsg(null); setMergePhone(""); setMode("merge"); }}>
            Merge another account
          </Button>
        </div>
      )}

      {mode === "change" && (
        <OtpNumberStep
          idPrefix="changeNumber"
          numberLabel="New number"
          sendLabel="Send codes"
          confirmLabel="Change number"
          send={sendPhoneChangeOtp}
          verify={(phone, otp, proof) => verifyPhoneChange(phone, otp, proof)}
          currentNumber={currentPhone ? formatPhone(currentPhone.replace(/\D/g, "")) : undefined}
          hasPasskey={hasPasskey}
          onDone={(data) => finish(data, "Number changed. Sign in with your new number from now on.", true)}
          onCancel={() => setMode("")}
          onPhoneInUse={(phone) => {
            setMergePhone(phone);
            setMode("merge");
          }}
        />
      )}

      {mode === "merge" && (
        <div>
          <p className="small mt-2 mb-1">
            Have another HKP account on a different number? Prove that number with a code and everything in it
            moves here: pieces, claims and its number history. That account then stops signing in.
            Duplicate pieces are archived, not deleted.
          </p>
          <OtpNumberStep
            idPrefix="mergeAccount"
            initialPhone={mergePhone}
            numberLabel="The other account's number"
            sendLabel="Send code to that number"
            confirmLabel="Merge into this account"
            send={sendMergeOtp}
            verify={(phone, otp) => verifyMerge(phone, otp)}
            onDone={(data) => {
              const moved = data?.merge?.itemsMoved ?? 0;
              const archived = data?.merge?.duplicatesArchived ?? 0;
              finish(
                data,
                `Accounts merged. ${moved} piece${moved === 1 ? "" : "s"} moved here` +
                  (archived ? `; ${archived} duplicate${archived === 1 ? "" : "s"} archived (see Archived items).` : "."),
              );
            }}
            onCancel={() => setMode("")}
          />
        </div>
      )}
    </section>
  );
};

export default CollectorPhoneSettings;
